import React, { useMemo } from 'react';
import styled from 'styled-components';
import { mediaQuery, typography } from '../../../../../theme';
import Markdown from 'markdown-to-jsx';
import { Primary } from '../../../../button';
import { useUser } from '../../../../../hooks/useUser';
import { fontSize } from '../../../../../theme/Typography';
import { FreeDeliveryCampaignWebshopViewModel } from '@kotipizzagroup/kotipizza-campaigns-api-client';
import { CurrencyFormatter } from '../../../../shoppingCart/components/orderItem/Currency';
import dayjs from '../../../../../utils/dayjs';
import { useAllRestaurants } from '../../../useRestaurants';
import { colors } from '../../../../../theme/color';

const freeDeliveryBannerIllustration = 'https://mob-cdn.azureedge.net/images/delivery.svg';

export const FreeDeliveryBanner: React.FC<{
  freeDeliveryCampaign?: FreeDeliveryCampaignWebshopViewModel;
}> = ({ freeDeliveryCampaign }) => {
  const { user, hasUserJoinedLoyaltyButNotLoggedInCurrently } = useUser();
  const { restaurants } = useAllRestaurants();

  const weekdayRestrictionText = useMemo((): string | null => {
    if (!freeDeliveryCampaign?.days?.length) return null;

    if (freeDeliveryCampaign.days.length === 1) {
      const weekday = dayjs().weekday(freeDeliveryCampaign.days[0]).format('dddd');
      return ` joka ${weekday}na`;
    }

    return ` ${dayjs().weekday(freeDeliveryCampaign.days[0]).format('dd')}–
    ${dayjs()
      .weekday(freeDeliveryCampaign.days[freeDeliveryCampaign.days.length - 1])
      .format('dd')}`;
  }, [freeDeliveryCampaign]);

  const assignedRestaurantsText = useMemo((): string | null => {
    if (!freeDeliveryCampaign?.assignedShops.length) return null;

    if (freeDeliveryCampaign.assignedShops.length === 1) {
      if (restaurants.length < 1) return null;

      const assignedShopId = freeDeliveryCampaign.assignedShops[0];
      const assignedShop = restaurants.find(
        (restaurant) => restaurant.restaurantId === assignedShopId
      );

      if (!assignedShop) return null;

      return `ravintolassa ${assignedShop.displayName}`;
    }

    return `${freeDeliveryCampaign.assignedShops.length} ravintolassa alueellasi`;
  }, [freeDeliveryCampaign, restaurants]);

  if (!freeDeliveryCampaign) return null;

  const { start, end, minimumOrderValue } = freeDeliveryCampaign;

  if (!start || !end) {
    return null;
  }

  return (
    <BannerWrapper>
      <Banner>
        <>
          <BannerTitle>
            <h4>
              {freeDeliveryCampaign.assignedShops.length > 0
                ? 'Paikallinen kuljetustarjous'
                : 'Ilmainen kuljetus'}
            </h4>
          </BannerTitle>
          <ul>
            <li>
              <b>Kuljetus 0 €</b>{' '}
              <span>
                {freeDeliveryCampaign.requireLogIn ? (
                  <>
                    <b>kirjautuneille</b>
                    <span> kanta-asiakkaille</span>{' '}
                  </>
                ) : (
                  ''
                )}
              </span>
              <span>{assignedRestaurantsText}</span>
            </li>
            <li>
              <span>
                Voimassa{weekdayRestrictionText} {dayjs(start).format('D.M.')}–
                {dayjs(end).format('D.M.YYYY')}
              </span>
            </li>
            {Boolean(minimumOrderValue > 0) && (
              <li>
                <span>Tilauksen loppusumma</span>{' '}
                <b>vähintään {CurrencyFormatter.format(minimumOrderValue || 0)}</b>
              </li>
            )}
            <li>Ennakkotilausten kuljetushinta 5,90–8,90 €</li>
          </ul>
          <BannerImageContainer>
            <img aria-hidden="true" src={freeDeliveryBannerIllustration} alt="" />
          </BannerImageContainer>
        </>

        {freeDeliveryCampaign.requireLogIn && !user?.subscribedToLoyalty && (
          <CampaignButton to={user ? '/rekisteroidy/syntymaaika' : '/kirjaudu'}>
            {hasUserJoinedLoyaltyButNotLoggedInCurrently
              ? 'Kirjaudu ja hyödynnä'
              : 'Liity kanta-asiakkaaksi'}
          </CampaignButton>
        )}
      </Banner>
    </BannerWrapper>
  );
};

export const BannerWrapper = styled.div`
  background: ${colors.white};
  position: relative;
  margin: 30px 0;

  @media ${mediaQuery.medium} {
    width: auto;
  }
`;

export const Banner = styled.div`
  background: ${colors.green.lighter(90)};
  background-size: 50% auto;
  box-shadow: 0 2px 4px rgb(0 0 0 / 10%);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  font-size: 0.875rem;
  color: ${colors.black};
  position: relative;
  overflow: hidden;

  & ul {
    margin-top: 4px;
    margin-left: 0;

    & > li:not(:last-child) {
      padding-bottom: 4px;
    }
  }
`;

export const BannerTitle = styled.div`
  padding: 16px 16px 4px 16px;
  text-align: left;

  & h4 {
    font-weight: 700;
    font-size: ${fontSize.MEDIUM};
    line-height: 125%;
    margin: 0;
  }
`;

export const BannerContent = styled(Markdown)`
  ${typography.paragraphMediumStyle}
  z-index: 2;
  text-align: left;
  margin-left: 0;
  width: 80%;
  font-family: var(--font-libre-franklin), system-ui, sans-serif;
  text-align: left;
  padding: 4px 16px 4px 16px;
  margin-bottom: 8px;

  & p {
    margin: 0.5rem 0;
  }

  & strong {
    color: ${colors.textSuccess};
    font-weight: 700;
  }

  @media ${mediaQuery.medium} {
    ${typography.paragraphSmallStyle}
    margin: 4px 0;
    width: 100%;
    max-width: none;

    & p {
      margin: 0.25rem 0;
    }
  }
`;

export const BannerLink = styled.div`
  margin: 0 16px 16px 16px;
  text-align: left;

  & a {
    ${typography.linkStyle}
    color: black;
  }

  & span {
    color: ${colors.black};
    font-size: ${fontSize.MEDIUM};
    line-height: 125%;
  }
`;

export const BannerImageContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  border-radius: 4px;
  width: 20%;
  position: absolute;
  right: -5px;
  bottom: -5px;
  transition: 0.1s height linear;

  & > img {
    height: 100%;
    width: 100%;
  }

  @media ${mediaQuery.medium} {
    & > img {
      display: none;
    }
  }
`;

export const CampaignButton = styled(Primary)`
  font-size: 0.875rem;
  padding: 0.375rem 1rem;
  margin: 0 16px 16px 16px;
  width: fit-content;
  z-index: 1;
  margin-top: 16px;

  @media ${mediaQuery.medium} {
    width: auto;
    align-self: stretch;
    padding: 0.75rem 1rem;
  }
`;
